import React from "react";
import { useTranslation } from "react-i18next";
import { useDashboardTheme } from "../context/DashboardThemeContext";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import whatsappLogo from "../../../assets/images/whatsapp.svg";

interface NewSessionModalProps {
  open: boolean;
  onClose: () => void;
  selectedApiType: string;
  onSelectedApiTypeChange: (value: string) => void;
  onContinue: () => void;
}

const OPTIONS = [
  {
    value: "API_OFFICIAL",
    label: "WhatsApp Business (API)",
    tooltipKey: "officialApiTooltip",
    useWhatsappIcon: true,
    flagKey: "whatsapp_connection" as const,
  },
  {
    value: "Waha",
    label: "WAHA",
    tooltipKey: "unofficialApiTooltip",
    useWhatsappIcon: false,
    flagKey: "waha_connection" as const,
  },
];

export const NewSessionModal: React.FC<NewSessionModalProps> = ({
  open,
  onClose,
  selectedApiType,
  onSelectedApiTypeChange,
  onContinue,
}) => {
  const { t } = useTranslation();
  const isDark = useDashboardTheme();
  const { hasFlag } = useFeatureFlags();
  const visibleOptions = OPTIONS.filter((opt) => hasFlag(opt.flagKey));

  const cardBg = isDark ? "rgba(30,30,30,0.95)" : "#ffffff";
  const cardBorder = (selected: boolean) =>
    selected
      ? "2px solid #4C3699"
      : isDark
        ? "2px solid rgba(255,255,255,0.1)"
        : "2px solid #e8e8e8";
  const cardShadow = (selected: boolean) =>
    selected ? "0 0 0 4px rgba(76,54,153,0.12)" : "0 2px 8px rgba(0,0,0,0.06)";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: isDark ? "rgba(18,18,18,0.98)" : "#f7f7f7",
          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #4C3699 0%, #25D366 100%)",
          px: 3,
          py: 2.5,
        }}
      >
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.2rem" }}>
          {t("New Session")}
        </Typography>
        <Typography
          sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.82rem", mt: 0.3 }}
        >
          {t("Choose the type of WhatsApp connection for your new session.")}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          {visibleOptions.map((opt) => {
            const selected = selectedApiType === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => onSelectedApiTypeChange(opt.value)}
                sx={{
                  flex: 1,
                  bgcolor: cardBg,
                  border: cardBorder(selected),
                  borderRadius: "14px",
                  boxShadow: cardShadow(selected),
                  cursor: "pointer",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.2,
                  position: "relative",
                  transition: "all 0.18s ease",
                  "&:hover": {
                    border: "2px solid #4C3699",
                    boxShadow: "0 0 0 4px rgba(76,54,153,0.08)",
                  },
                }}
              >
                {/* Checkmark */}
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    fontSize: "1.2rem",
                    color: selected
                      ? "#4C3699"
                      : isDark
                        ? "rgba(255,255,255,0.1)"
                        : "#e0e0e0",
                    transition: "color 0.18s ease",
                  }}
                />

                {/* Icon circle */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: selected
                      ? opt.useWhatsappIcon
                        ? "linear-gradient(135deg, #25D366 0%, #128C7E 100%)"
                        : "linear-gradient(135deg, #4C3699 0%, #6b4fd8 100%)"
                      : isDark
                        ? opt.useWhatsappIcon
                          ? "rgba(37,211,102,0.12)"
                          : "rgba(76,54,153,0.15)"
                        : opt.useWhatsappIcon
                          ? "rgba(37,211,102,0.08)"
                          : "rgba(76,54,153,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.18s ease",
                    border: selected
                      ? "none"
                      : isDark
                        ? opt.useWhatsappIcon
                          ? "1.5px solid rgba(37,211,102,0.25)"
                          : "1.5px solid rgba(76,54,153,0.35)"
                        : opt.useWhatsappIcon
                          ? "1.5px solid rgba(37,211,102,0.2)"
                          : "1.5px solid rgba(76,54,153,0.2)",
                  }}
                >
                  {opt.useWhatsappIcon ? (
                    <img
                      src={whatsappLogo}
                      alt="WhatsApp"
                      style={{ width: 36, height: 36 }}
                    />
                  ) : (
                    <ForumRoundedIcon
                      sx={{
                        fontSize: "2rem",
                        color: selected
                          ? "#fff"
                          : isDark
                            ? "#9b7fe8"
                            : "#4C3699",
                        transition: "color 0.18s ease",
                      }}
                    />
                  )}
                </Box>

                {/* Title + help button */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      color: selected ? "#4C3699" : isDark ? "#e0e0e0" : "#222",
                      textAlign: "center",
                      lineHeight: 1.3,
                      transition: "color 0.18s ease",
                    }}
                  >
                    {opt.label}
                  </Typography>
                  <Tooltip
                    title={t(opt.tooltipKey)}
                    arrow
                    placement="top"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <HelpOutlineIcon
                      sx={{
                        fontSize: "0.95rem",
                        color: isDark ? "#666" : "#aaa",
                        cursor: "help",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: isDark ? "#a0a0a0" : "#6E6E6E",
            textTransform: "none",
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
            },
          }}
        >
          {t("Close")}
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={!selectedApiType}
          sx={{
            background: "linear-gradient(135deg, #4C3699 0%, #6b4fd8 100%)",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderRadius: "8px",
            "&:hover": {
              background: "linear-gradient(135deg, #3d2a80 0%, #5a3fc2 100%)",
            },
          }}
        >
          {t("Continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
